import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Boxes, Search, ArrowRight, Star, Package } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Public catalog storefront for the 3D printing side. Lists every
 * published item admins have staged, with a search box and category
 * filter. Clicking an item hands off to /prints/quote?catalog=<id>
 * where the existing quote engine hydrates from the catalog file so
 * the customer only picks scale + material + quantity.
 */
export default function PrintsShopPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/prints/catalog");
        if (!cancelled) setItems(r.data.items || []);
      } catch (err) {
        toast.error(apiError(err, "Couldn't load catalog"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const categories = useMemo(() => {
    const set = new Set(items.map((i) => i.category).filter(Boolean));
    return ["all", ...Array.from(set)];
  }, [items]);

  const filtered = useMemo(() => {
    let list = items;
    if (category !== "all") list = list.filter((i) => i.category === category);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((i) =>
        (i.name || "").toLowerCase().includes(q) ||
        (i.description || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [items, category, search]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="prints-shop-page">
      <div className="flex flex-col gap-2 mb-6 sm:mb-8">
        <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-fuchsia-300/80">Print Shop</span>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[0.05em] text-white leading-[1.05]">
          READY-TO-PRINT MODELS
        </h1>
        <p className="text-white/60 text-sm sm:text-base max-w-2xl">
          Curated STL / OBJ files from our library. Pick one, choose your size and material, and we'll print, cure and ship it.
          Prefer to send your own file? <Link to="/prints/quote" className="text-fuchsia-300 hover:text-fuchsia-200 underline underline-offset-4">Upload STL / OBJ →</Link>
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="prints-shop-filters">
        {categories.map((c) => (
          <button
            key={c}
            data-testid={`prints-shop-cat-${c}`}
            onClick={() => setCategory(c)}
            className={`font-mono text-[10px] uppercase tracking-[0.28em] px-3 py-1.5 rounded-full border transition-colors ${
              category === c
                ? "border-fuchsia-300/60 bg-fuchsia-400/10 text-fuchsia-300"
                : "border-white/10 text-white/50 hover:text-white hover:border-white/30"
            }`}
          >
            {c}
          </button>
        ))}
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input
            data-testid="prints-shop-search"
            placeholder="Search models…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/30 h-9"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-white/50 text-sm" data-testid="prints-shop-loading">Loading catalog…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-[hsl(222_22%_7%)]/80 p-10 text-center" data-testid="prints-shop-empty">
          <Boxes className="mx-auto h-10 w-10 text-fuchsia-300/60 mb-3" />
          <div className="text-white/70 text-base">
            {items.length === 0 ? "No models in the catalog yet." : "No models match your filters."}
          </div>
          <div className="mt-3">
            <Link to="/prints/quote">
              <Button size="sm" className="bg-fuchsia-400 text-black hover:bg-fuchsia-300">
                Upload your own STL / OBJ
              </Button>
            </Link>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="prints-shop-grid">
          {filtered.map((it) => (
            <CatalogCard key={it.id} item={it} />
          ))}
        </div>
      )}
    </div>
  );
}

function CatalogCard({ item }) {
  return (
    <Link
      to={`/prints/quote?catalog=${item.id}`}
      data-testid={`prints-shop-item-${item.id}`}
      className="group relative rounded-2xl border border-white/10 bg-[hsl(222_22%_7%)]/80 overflow-hidden hover:border-fuchsia-300/40 transition-colors"
    >
      <div className="relative aspect-square bg-gradient-to-br from-fuchsia-400/5 to-amber-400/5 overflow-hidden">
        {item.preview_image_url ? (
          <img
            src={item.preview_image_url}
            alt={item.name}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Package className="h-14 w-14 text-fuchsia-300/40" />
          </div>
        )}
        {item.featured && (
          <div className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full border border-amber-300/40 bg-amber-400/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.22em] text-amber-300">
            <Star className="h-2.5 w-2.5" /> Featured
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="text-white font-display text-base truncate" title={item.name}>{item.name}</div>
        {item.description && (
          <div className="mt-0.5 text-[11px] text-white/50 line-clamp-2">{item.description}</div>
        )}
        <div className="mt-2 flex items-center justify-between">
          <div className="font-mono text-sm text-fuchsia-300 tabular-nums">
            {item.base_price_usd != null ? `$${item.base_price_usd.toFixed(2)}` : "Custom quote"}
          </div>
          <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] text-white/60 group-hover:text-fuchsia-300">
            Start <ArrowRight className="h-3 w-3" />
          </span>
        </div>
      </div>
    </Link>
  );
}
